import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";


//App
import organizationApi from "../../../app/model/api/organizationApi";
import { queryKeys } from ".././constants";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../../utility/constants/constants";
import { SearchValue } from "../../../store/types";
import { SortBy } from "../../../types";

import {
    selectSearchValue,
    setSearchValue,
} from "../../../store/campaign";
import Donors from "./Donors";
import { DonorCampaignContextData, DonorCampaignUseState } from "../../../types/donorCampaign";
import { useNavigate, useParams } from "react-router-dom";

export const DonorCampaignContext = React.createContext<DonorCampaignContextData | null>(
    null
);

const DonorsContainer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [state, setState] = useState<DonorCampaignUseState>({
        campaignId: null,
        activeToggle: true,
        searchPayload: useSelector(selectSearchValue) ?? null,
        pageSize: GRID_PAGE_SIZE,
        currentPage: 1,
        sortBy: [{ field: "firstname", direction: 1 }],
    });

    const donorsCampaignGridQuery = useQuery({
        queryKey: [queryKeys.searchDonorCampaign],
        queryFn: () =>
            organizationApi.searchDonors({
                ...state.searchPayload,
                campaignId: id,
                isActive: true,
                pageSize: state.pageSize,
                pageNumber: state.currentPage,
                sortBy: state.sortBy,
            }),
    });

    const campaignQuery = useQuery({
        queryKey: [queryKeys.getOrganizationCampaign, state.campaignId],
        queryFn: () =>
            organizationApi.getOrganizationCampaign(id ?? EMPTY_GUID),
        enabled: id !== null,
    });

    const updateCampaignId = async () => { };

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values?.name) {
            dispatch(setSearchValue({ name: values.name }));
        }
        if (values && Object.keys(values).length) {
            setState((prevState) => ({
                ...prevState,
                searchPayload: values,
                currentPage: 1,
            }));
        } else {
            setState((prevState) => ({
                ...prevState,
                searchPayload: null,
                currentPage: 1,
                sortBy: [{ field: "name", direction: 1 }],
            }));
        }
    };

    const updateCurrentPage = async (currentPage: number) => {
        setState((prevState) => ({
            ...prevState,
            currentPage: currentPage,
        }));
    };

    const updateCurrentPageSize = async (pageSize: number) => {
        setState((prevState) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const updateSortBy = async (sortBy: SortBy) => {
        const { field, direction } = sortBy;
        setState((prevState) => ({
            ...prevState,
            sortBy: [{ field, direction }],
        }));
    };

    const handleOnBackClick = () => navigate(-1);

    const contextValue: DonorCampaignContextData = {
        data: undefined,
        id: state.campaignId,
        pageSize: state.pageSize,
        activeToggle: state.activeToggle,
        setId: updateCampaignId,
        refetchGrid: () => donorsCampaignGridQuery.refetch(),
        setSearchValue: updateSearchPayload,
        setCurrentPage: updateCurrentPage,
        setSortBy: updateSortBy,
        setCurrentPageSize: updateCurrentPageSize,
        paging: {
            currentPage: state.currentPage,
            totalSize: donorsCampaignGridQuery.data?.totalSize ?? 0,
        },
    };
    const campaignName = campaignQuery?.data?.result?.name ?? "";

    return donorsCampaignGridQuery.isLoading ? (
        <LoadingIndicator />
    ) : (
        <DonorCampaignContext.Provider value={contextValue}>
            <Donors
                campaignName={campaignName}
                data={donorsCampaignGridQuery?.data?.result}
                activateMutation={() => {}}
                deleteMutation={() => {}}
                handleOnClose={handleOnBackClick}
            />
        </DonorCampaignContext.Provider>
    );
};

export default DonorsContainer;
